<template>
  <div class="rounded-t-md rounded-bl-md w-full overflow-hidden h-full">
    <div class="flex items-stretch bg-grey-200 w-full h-full">
      <PButton
        size="icon"
        @click="togglePlayer"
        :aria-label="playing ? 'mettre en pause' : 'démarrer la lecture'"
        class="-outline-offset-2 h-3"
        :data-sentry-id="`AudioPlayerToggle-${playing}`"
      >
        <OhVueIcon :name="playing ? pauseIcon : playIcon" />
      </PButton>
      <div
        class="progress-bar grow bg-grey-925 flex items-stretch cursor-pointer -outline-offset-1"
        @click="onProgressBarClick"
        role="slider"
        aria-label="Barre de lecture"
        :aria-valuemin="0"
        :aria-valuemax="valueMax"
        :aria-valuenow="valueNow"
        :aria-valuetext="valueText"
        tabindex="0"
        @keydown.left.exact="onLeft(KEYBOARD_SMALL_SEEK)"
        @keydown.right.exact="onRight(KEYBOARD_SMALL_SEEK)"
        @keydown.alt.left="onLeft(KEYBOARD_BIG_SEEK)"
        @keydown.alt.right="onRight(KEYBOARD_BIG_SEEK)"
        @keydown.space="togglePlayer"
        data-sentry-id="AudioPlayerProgressBar"
      >
        <div :style="{ width: `${progress}%` }" class="bg-blue-cumulus-main-526"></div>
      </div>
      <slot
        name="time"
        :currentMinutes="currentMinutes"
        :currentSeconds="currentSeconds"
        :totalMinutes="totalMinutes"
        :totalSeconds="totalSeconds"
      >
        <p class="px-1 text-sm font-bold self-center whitespace-nowrap text-grey-975">
          <span>{{ currentMinutes }}:{{ currentSeconds }}</span> /
          <span>{{ totalMinutes }}:{{ totalSeconds }}</span>
        </p>
      </slot>
    </div>
    <audio
      ref="audioEl"
      preload="metadata"
      @play="onAudioPlay"
      @pause="onAudioPause"
      @ended="onAudioEnded"
      @loadedmetadata="onMetadata"
      @timeupdate="onTimeUpdate"
    >
      <source type="audio/webm" :src="src" />
      Votre navigateur ne peut pas lire le message audio
    </audio>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { PButton, useIcons } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { RiPlayFill, RiPauseFill } from "oh-vue-icons/icons";
import { get } from "lodash";
import { formatSeconds, formatMinutes } from "../utils";

interface Props {
  src: string;
  modelValue: number;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:modelValue", value: number): void;
}>();

const [playIcon, pauseIcon] = useIcons(RiPlayFill, RiPauseFill);

const audioEl = ref<HTMLAudioElement | null>(null);

// accessibility variables
const KEYBOARD_SMALL_SEEK = 5;
const KEYBOARD_BIG_SEEK = 60;
const valueMax = ref(0);
const valueNow = ref(0);
const valueText = ref("");

const PROGRESS_INTERVAL = 40; // 25 fps
let timer: number | null = null;
function startTimer() {
  clearTimer();
  timer = window.setInterval(updateProgress, PROGRESS_INTERVAL);
}

function clearTimer() {
  if (timer) {
    window.clearInterval(timer);
    timer = null;
  }
}

const progress = ref(0);
function updateProgress() {
  progress.value = (audioEl.value!.currentTime / audioEl.value!.duration || 0) * 100;
  updateCurrentTime();
}

async function togglePlayer() {
  if (playing.value) {
    await pause();
  } else {
    await play();
  }
}

async function play() {
  await audioEl.value?.play();
  startTimer();
}

async function pause() {
  await audioEl.value?.pause();
}

const playing = ref(false);
function onAudioPlay() {
  playing.value = true;
}

function onAudioPause() {
  playing.value = false;
  clearTimer();
}

function onAudioEnded() {
  playing.value = false;
  clearTimer();
  updateProgress();
}

const totalMinutes = ref("0");
const totalSeconds = ref("00");
function onMetadata(e: Event) {
  const duration = get(e, "target.duration", 0) * 1_000;
  totalMinutes.value = formatMinutes(duration, { noPaddingBelow10: true });
  totalSeconds.value = formatSeconds(duration);
  valueMax.value = Math.floor(duration / 1_000); // total number of seconds
}

const currentMinutes = ref("0");
const currentSeconds = ref("00");
function updateCurrentTime() {
  const currentTime = audioEl.value!.currentTime;
  const currentTimeMs = audioEl.value!.currentTime * 1_000;
  currentMinutes.value = formatMinutes(currentTimeMs, {
    noPaddingBelow10: true,
  });
  currentSeconds.value = formatSeconds(currentTimeMs);
  valueNow.value = Math.floor(currentTime);
  valueText.value = `${currentMinutes.value} minute(s) ${currentSeconds.value} seconde(s) sur ${totalMinutes.value} minute(s) ${totalSeconds.value} seconde(s)`;
}

function seek(value: number) {
  audioEl.value!.currentTime = value;
  updateProgress();
}

async function onProgressBarClick(event: MouseEvent) {
  const divRect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
  const seekPercent = (event.clientX - divRect.left) / divRect.width;
  seek(audioEl.value!.duration * seekPercent);
}

function onLeft(jumpValue: number) {
  const target = Math.max(0, audioEl.value!.currentTime - jumpValue);
  seek(target);
}

function onRight(jumpValue: number) {
  const target = Math.min(audioEl.value!.duration, audioEl.value!.currentTime + jumpValue);
  seek(target);
}

function onTimeUpdate(_e: Event) {
  emit("update:modelValue", audioEl.value!.currentTime);
}

watch(
  () => props.modelValue,
  val => {
    if (val !== audioEl.value?.currentTime) {
      seek(val);
    }
  }
);

onMounted(async () => {
  if (props.modelValue > 0) {
    await new Promise(resolve => {
      audioEl.value!.onloadedmetadata = () => resolve(true);
    });
    seek(props.modelValue);
  }
});
</script>
